/* ============================================================================
 * CalculatorPip — a draggable, always-on-top picture-in-picture calculator.
 *
 * Mounted once in DashboardLayout so it's available across the authenticated
 * app. A floating button opens a small panel you can drag anywhere and keep
 * open while building an estimate. Two tabs:
 *   - Calculator  — standard number pad (safe expression evaluator, no eval).
 *   - Quick calcs — construction formulas: concrete (cubic yards), wall area,
 *     room surface area, stairs, common rafter.
 *
 * Self-contained: pointer-event dragging (mouse + touch), no extra deps.
 * ========================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Calculator as CalcIcon,
  X,
  GripHorizontal,
  Copy,
  Check,
  Ruler,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/* ----------------------------------------------------------------------------
 * Formatting helpers
 * ------------------------------------------------------------------------- */
const round = (n, d = 2) => {
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

// Decimal feet → feet + inches string, e.g. 12.54 → 12' 6 1/2"
const ftIn = (decimalFeet) => {
  if (!Number.isFinite(decimalFeet)) return '—';
  const neg = decimalFeet < 0 ? '-' : '';
  const abs = Math.abs(decimalFeet);
  const whole = Math.floor(abs);
  let inches = (abs - whole) * 12;
  // round inches to nearest 1/8"
  let eighths = Math.round(inches * 8);
  let ft = whole;
  if (eighths === 96) {
    ft += 1;
    eighths = 0;
  }
  const inWhole = Math.floor(eighths / 8);
  const frac = eighths % 8;
  const fracStr = frac === 0 ? '' : ` ${frac}/8`;
  const reduced = fracStr
    .replace(' 4/8', ' 1/2')
    .replace(' 2/8', ' 1/4')
    .replace(' 6/8', ' 3/4')
    .replace(' 0/8', '');
  return `${neg}${ft}' ${inWhole}${reduced}"`;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/* ----------------------------------------------------------------------------
 * Safe arithmetic evaluator (+ - * / with precedence, parentheses). No eval.
 * ------------------------------------------------------------------------- */
const evalExpr = (expr) => {
  const tokens = expr.match(/(\d+\.?\d*|\.\d+|[+\-*/()])/g);
  if (!tokens) return null;
  let i = 0;
  const peek = () => tokens[i];
  const next = () => tokens[i++];

  const parseExpression = () => {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = next();
      const rhs = parseTerm();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  };
  const parseTerm = () => {
    let value = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const op = next();
      const rhs = parseFactor();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  };
  const parseFactor = () => {
    const tok = peek();
    if (tok === '(') {
      next();
      const v = parseExpression();
      if (peek() === ')') next();
      return v;
    }
    if (tok === '-') {
      next();
      return -parseFactor();
    }
    if (tok === '+') {
      next();
      return parseFactor();
    }
    return parseFloat(next());
  };

  try {
    const result = parseExpression();
    if (i !== tokens.length) return null;
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};

/* ----------------------------------------------------------------------------
 * Result row — labeled value + copy button
 * ------------------------------------------------------------------------- */
const ResultRow = ({ label, value, emphasize }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = String(value).replace(/[^0-9./'"\- ]/g, '');
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text.trim());
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ${
        emphasize
          ? 'bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30'
          : 'bg-[hsl(var(--muted))]'
      }`}
    >
      <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className={`text-sm font-semibold tabular-nums ${
            emphasize ? 'text-[hsl(var(--primary))]' : 'text-foreground'
          }`}
        >
          {value}
        </span>
        <button
          type="button"
          onClick={copy}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Copy result"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </span>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * A small labeled numeric input
 * ------------------------------------------------------------------------- */
const NumField = ({ label, value, onChange, suffix, placeholder }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
      {label}
    </span>
    <div className="relative mt-0.5">
      <Input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 bg-[hsl(var(--muted))] border-border text-foreground text-sm pr-9"
      />
      {suffix ? (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
          {suffix}
        </span>
      ) : null}
    </div>
  </label>
);

/* ----------------------------------------------------------------------------
 * Quick-calc definitions
 * ------------------------------------------------------------------------- */
const QUICK_CALCS = {
  concrete: {
    label: 'Concrete (yd³)',
    fields: [
      { key: 'l', label: 'Length', suffix: 'ft' },
      { key: 'w', label: 'Width', suffix: 'ft' },
      { key: 't', label: 'Thickness', suffix: 'in' },
    ],
    compute: ({ l, w, t }) => {
      const cy = (num(l) * num(w) * (num(t) / 12)) / 27;
      return [
        { label: 'Cubic yards', value: `${round(cy, 2)} yd³`, emphasize: true },
        { label: '+10% waste', value: `${round(cy * 1.1, 2)} yd³` },
        { label: 'Cubic feet', value: `${round(cy * 27, 1)} ft³` },
      ];
    },
  },
  wall: {
    label: 'Wall area',
    hint: 'Rectangular room — all four walls.',
    fields: [
      { key: 'l', label: 'Room length', suffix: 'ft' },
      { key: 'w', label: 'Room width', suffix: 'ft' },
      { key: 'h', label: 'Wall height', suffix: 'ft' },
      { key: 'o', label: 'Deduct openings', suffix: 'ft²' },
    ],
    compute: ({ l, w, h, o }) => {
      const perimeter = 2 * (num(l) + num(w));
      const gross = perimeter * num(h);
      const net = Math.max(0, gross - num(o));
      return [
        { label: 'Net wall area', value: `${round(net, 1)} ft²`, emphasize: true },
        { label: 'Perimeter', value: `${round(perimeter, 1)} ft` },
        { label: 'Gross (no deduct)', value: `${round(gross, 1)} ft²` },
      ];
    },
  },
  surface: {
    label: 'Surface area',
    hint: 'Walls + ceiling — paint / drywall.',
    fields: [
      { key: 'l', label: 'Room length', suffix: 'ft' },
      { key: 'w', label: 'Room width', suffix: 'ft' },
      { key: 'h', label: 'Wall height', suffix: 'ft' },
      { key: 'o', label: 'Deduct openings', suffix: 'ft²' },
    ],
    compute: ({ l, w, h, o }) => {
      const walls = 2 * (num(l) + num(w)) * num(h);
      const ceiling = num(l) * num(w);
      const net = Math.max(0, walls + ceiling - num(o));
      return [
        { label: 'Walls + ceiling', value: `${round(net, 1)} ft²`, emphasize: true },
        { label: 'Walls only', value: `${round(Math.max(0, walls - num(o)), 1)} ft²` },
        { label: 'Ceiling', value: `${round(ceiling, 1)} ft²` },
      ];
    },
  },
  stairs: {
    label: 'Stairs',
    hint: 'Enter total floor-to-floor rise.',
    fields: [
      { key: 'rise', label: 'Total rise', suffix: 'in' },
      { key: 'tread', label: 'Tread depth', suffix: 'in', default: '10.5' },
      { key: 'target', label: 'Target riser', suffix: 'in', default: '7' },
    ],
    compute: ({ rise, tread, target }) => {
      const totalRise = num(rise);
      const treadDepth = num(tread) || 10.5;
      const targetRiser = num(target) || 7;
      if (totalRise <= 0) return [];
      const risers = Math.max(1, Math.round(totalRise / targetRiser));
      const riserH = totalRise / risers;
      const treads = risers - 1;
      const totalRun = treads * treadDepth;
      const stringer = Math.sqrt(totalRise ** 2 + totalRun ** 2);
      const ok = riserH >= 4 && riserH <= 7.75 && treadDepth >= 10;
      return [
        { label: 'Risers', value: `${risers} @ ${round(riserH, 2)}"`, emphasize: true },
        { label: 'Treads', value: `${treads}` },
        { label: 'Total run', value: ftIn(totalRun / 12) },
        { label: 'Stringer', value: ftIn(stringer / 12) },
        { label: 'Code (7¾″/10″)', value: ok ? 'OK ✓' : 'Check ⚠' },
      ];
    },
  },
  rafter: {
    label: 'Rafter',
    hint: 'Common rafter from span + pitch.',
    fields: [
      { key: 'span', label: 'Building span', suffix: 'ft' },
      { key: 'pitch', label: 'Pitch (rise/12)', suffix: '/12' },
      { key: 'overhang', label: 'Overhang', suffix: 'in', default: '0' },
    ],
    compute: ({ span, pitch, overhang }) => {
      const run = num(span) / 2;
      const p = num(pitch);
      if (run <= 0 || p <= 0) return [];
      const rise = run * (p / 12);
      const line = Math.sqrt(run ** 2 + rise ** 2);
      const total = line + num(overhang) / 12;
      return [
        { label: 'Rafter length', value: ftIn(total), emphasize: true },
        { label: 'Line length', value: ftIn(line) },
        { label: 'Rise', value: ftIn(rise) },
        { label: 'Run', value: ftIn(run) },
      ];
    },
  },
};

const QUICK_ORDER = ['concrete', 'wall', 'surface', 'stairs', 'rafter'];

/* ----------------------------------------------------------------------------
 * Quick calc pane
 * ------------------------------------------------------------------------- */
const QuickCalcPane = () => {
  const [active, setActive] = useState('concrete');
  const [values, setValues] = useState({});

  const calc = QUICK_CALCS[active];
  const vals = values[active] || {};
  const setVal = (key, v) =>
    setValues((prev) => ({
      ...prev,
      [active]: { ...(prev[active] || {}), [key]: v },
    }));

  // Seed defaults for fields that declare one.
  const effective = { ...vals };
  calc.fields.forEach((f) => {
    if (f.default !== undefined && (effective[f.key] === undefined || effective[f.key] === '')) {
      effective[f.key] = vals[f.key] === '' ? '' : (vals[f.key] ?? f.default);
    }
  });

  const results = calc.compute(effective);

  return (
    <div className="space-y-3">
      <Select value={active} onValueChange={(v) => setActive(v)}>
        <SelectTrigger className="h-8 bg-[hsl(var(--muted))] border-border text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[hsl(var(--card))] border-border">
          {QUICK_ORDER.map((k) => (
            <SelectItem key={k} value={k}>
              {QUICK_CALCS[k].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {calc.hint ? (
        <p className="text-[11px] text-muted-foreground -mt-1">{calc.hint}</p>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        {calc.fields.map((f) => (
          <NumField
            key={f.key}
            label={f.label}
            suffix={f.suffix}
            value={effective[f.key] ?? ''}
            placeholder={f.default !== undefined ? f.default : '0'}
            onChange={(v) => setVal(f.key, v)}
          />
        ))}
      </div>

      <div className="space-y-1.5 pt-1">
        {results.length > 0 ? (
          results.map((r, idx) => (
            <ResultRow
              key={idx}
              label={r.label}
              value={r.value}
              emphasize={r.emphasize}
            />
          ))
        ) : (
          <p className="text-[11px] italic text-muted-foreground text-center py-2">
            Enter dimensions to calculate.
          </p>
        )}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Basic calculator pane
 * ------------------------------------------------------------------------- */
const KEYS = [
  ['C', '(', ')', '⌫'],
  ['7', '8', '9', '/'],
  ['4', '5', '6', '*'],
  ['1', '2', '3', '-'],
  ['0', '.', '=', '+'],
];

const BasicCalcPane = () => {
  const [expr, setExpr] = useState('');
  const [result, setResult] = useState(null);

  const press = (k) => {
    if (k === 'C') {
      setExpr('');
      setResult(null);
      return;
    }
    if (k === '⌫') {
      setResult(null);
      setExpr((e) => e.slice(0, -1));
      return;
    }
    if (k === '=') {
      const r = evalExpr(expr);
      setResult(r === null ? 'Error' : String(round(r, 6)));
      return;
    }
    // After a result is shown: an operator continues from it; a digit/paren
    // starts a fresh expression.
    if (result !== null) {
      const cont = ['+', '-', '*', '/'].includes(k) && result !== 'Error';
      setExpr(cont ? result + k : k.replace(/^([*/])/, ''));
      setResult(null);
      return;
    }
    setExpr((e) => (e + k).replace(/^([*/])/, ''));
  };

  const display = result !== null ? result : expr || '0';
  const [copied, setCopied] = useState(false);
  const copyResult = () => {
    if (navigator?.clipboard?.writeText && result !== null && result !== 'Error') {
      navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-md bg-[hsl(var(--muted))] border border-border px-3 py-2 min-h-[2.75rem] flex items-center justify-end">
        <span className="text-lg font-semibold tabular-nums text-foreground truncate" data-testid="calc-display">
          {display}
        </span>
        {result !== null && result !== 'Error' ? (
          <button
            type="button"
            onClick={copyResult}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Copy result"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {KEYS.flat().map((k) => {
          const isOp = ['/', '*', '-', '+', '='].includes(k);
          const isClear = k === 'C' || k === '⌫';
          return (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              className={`h-9 rounded-md text-sm font-semibold transition-colors ${
                k === '='
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90'
                  : isOp
                  ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20'
                  : isClear
                  ? 'bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'
                  : 'bg-[hsl(var(--muted))] text-foreground hover:bg-foreground/10'
              }`}
              data-testid={`calc-key-${k}`}
            >
              {k}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * The PIP shell — floating button + draggable panel
 * ------------------------------------------------------------------------- */
const PANEL_W = 300;

export const CalculatorPip = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('quick');
  const [pos, setPos] = useState(null); // {x,y}; null = default bottom-right
  const drag = useRef(null);

  const onPointerDown = (e) => {
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    drag.current = {
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = useCallback((e) => {
    if (!drag.current) return;
    const x = e.clientX - drag.current.dx;
    const y = e.clientY - drag.current.dy;
    const maxX = window.innerWidth - PANEL_W - 8;
    const maxY = window.innerHeight - 80;
    setPos({
      x: Math.min(Math.max(8, x), Math.max(8, maxX)),
      y: Math.min(Math.max(8, y), Math.max(8, maxY)),
    });
  }, []);
  const onPointerUp = (e) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keep the panel on-screen if the window resizes.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => {
      setPos((p) =>
        p
          ? {
              x: Math.min(p.x, Math.max(8, window.innerWidth - PANEL_W - 8)),
              y: Math.min(p.y, Math.max(8, window.innerHeight - 80)),
            }
          : p
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pos]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-lg flex items-center justify-center hover:opacity-90 print:hidden"
        aria-label="Open calculator"
        data-testid="calc-pip-open"
      >
        <CalcIcon size={20} />
      </button>
    );
  }

  const style = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : { right: 16, bottom: 16 };

  return (
    <div
      className="fixed z-50 rounded-lg border border-border bg-[hsl(var(--card))] shadow-2xl print:hidden"
      style={{ width: PANEL_W, ...style }}
      data-testid="calc-pip-panel"
    >
      {/* Drag handle / header */}
      <div className="relative flex items-center justify-between px-3 py-2 border-b border-border">
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="flex items-center gap-2 flex-1 cursor-move touch-none select-none"
          data-testid="calc-pip-drag"
        >
          <GripHorizontal size={14} className="text-muted-foreground" />
          <span className="text-xs uppercase tracking-[0.16em] font-semibold text-foreground">
            Calculator
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close calculator"
          data-testid="calc-pip-close"
        >
          <X size={15} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {[
          { key: 'quick', label: 'Quick calcs', icon: Ruler },
          { key: 'calc', label: 'Calculator', icon: CalcIcon },
        ].map((tb) => {
          const Icon = tb.icon;
          const activeTab = tab === tb.key;
          return (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold border-b-2 transition-colors ${
                activeTab
                  ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`calc-tab-${tb.key}`}
            >
              <Icon size={12} />
              {tb.label}
            </button>
          );
        })}
      </div>

      <div className="p-3 max-h-[70vh] overflow-y-auto">
        {tab === 'quick' ? <QuickCalcPane /> : <BasicCalcPane />}
      </div>
    </div>
  );
};

export default CalculatorPip;
